import { isAnimatedImageSrc } from './utils';
import { isOwnStorageUrl } from './storage-url';

/**
 * Whether next/image should hand `src` to the browser untouched
 * (`unoptimized`) instead of sending it through /_next/image.
 *
 * Image optimisation is on in production (next.config.js), and its
 * `remotePatterns` admit only this project's two Firebase Storage buckets and
 * placehold.co. Anything else answers 400 from the optimizer, which the
 * visitor sees as a broken image. Stored image URLs are not all ours:
 * accounts created before 2026-01-13 carry their Google profile photo
 * (`*.googleusercontent.com`, which firestore.rules still allow for avatars
 * and banners), and older client code saved covers from other hosts. Those
 * are served as they always were, straight from their host, which is what
 * every image got before optimisation was switched on. Widening
 * `remotePatterns` instead would let anyone make our server fetch and decode
 * images from those hosts.
 *
 * Animated GIFs skip it too: the optimizer would freeze them.
 *
 * Mirrors next.config.js's remotePatterns; product-designability.test.ts
 * checks the two agree.
 */
export function skipImageOptimizer(src: unknown): boolean {
  if (typeof src !== 'string' || src === '') return false;
  if (isAnimatedImageSrc(src)) return true;
  // Same-origin files are optimised; data: and blob: URLs next/image already
  // passes through unoptimised on its own.
  if (src.startsWith('/') || src.startsWith('data:') || src.startsWith('blob:')) return false;
  let parsed: URL;
  try {
    parsed = new URL(src);
  } catch {
    return true;
  }
  if (parsed.protocol !== 'https:') return true;
  if (parsed.hostname === 'placehold.co') return false;
  // Only the firebasestorage host style: remotePatterns does not list
  // storage.googleapis.com, although isOwnStorageUrl understands it.
  return !(parsed.hostname === 'firebasestorage.googleapis.com' && isOwnStorageUrl(parsed));
}
